/**
 * FunnelBanner
 *
 * Inline banner for collection page funnel experiences.
 * Supports page_content blocks when configured, falls back to hardcoded layout.
 */

import styled from '@emotion/styled';
import type { CSSProperties } from 'react';
import type { FunnelOffer, StepPageContent, StepPageBlock, DisplaySettings } from '../types';
import type { BlockCallbacks } from './ComponentRenderers';
import { normalizeBlocks, renderBlocks, blocksHaveCta } from './BlockRenderer';
import { withResolvedFunnelOfferPrice } from '../offer-pricing';

const BannerContainer = styled.div`
  border-radius: 12px;
  margin: 0 0 24px;
  position: relative;
  overflow: hidden;
  animation: bannerSlideIn 0.3s ease;

  @keyframes bannerSlideIn {
    from { transform: translateY(-10px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const ClassicBannerLayout = styled.div`
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  border-radius: 12px;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    flex-direction: column;
    text-align: center;
  }
`;

const BlockContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
`;

const BannerImage = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const BannerContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const BannerHeadline = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: white;
  margin: 0 0 4px;
`;

const BannerPricing = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
`;

const BannerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const CTAButton = styled.button`
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 700;
  color: #4f46e5;
  background: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s;

  &:hover { opacity: 0.9; }
`;

const DeclineLink = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  white-space: nowrap;

  &:hover { color: white; }
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  line-height: 1;

  &:hover { color: white; }
`;

const BlockDismissButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  z-index: 1;

  &:hover { color: ${({ theme }) => theme.colors.text.primary}; }
`;

const FallbackActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 0 16px 16px;
`;

const FallbackCTA = styled.button`
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 700;
  color: white;
  background: ${({ theme }) => theme.colors.text.primary};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover { opacity: 0.9; }
`;

const FallbackDecline = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
`;

interface FunnelBannerProps {
  offer: FunnelOffer;
  pageContent?: StepPageContent | StepPageBlock[] | null;
  displaySettings?: DisplaySettings | null;
  containerStyle?: CSSProperties;
  errorMessage?: string | null;
  onAccept: () => void;
  onDecline: () => void;
  onDismiss: () => void;
  accepting?: boolean;
  suppressOfferActions?: boolean;
}

function safePrice(value: number | null | undefined): string {
  return Number.isFinite(value) ? `$${(value as number).toFixed(2)}` : '$0.00';
}

export function FunnelBanner({ offer, pageContent, displaySettings: _displaySettings, containerStyle, errorMessage, onAccept, onDecline, onDismiss, accepting, suppressOfferActions }: FunnelBannerProps) {
  // displaySettings available for future banner customization (height, position, etc.)
  void _displaySettings;
  const displayOffer = withResolvedFunnelOfferPrice(offer);
  const blocks = normalizeBlocks(pageContent);
  const hasBlocks = blocks.length > 0;
  const hasCtaBlock = hasBlocks && blocksHaveCta(blocks);
  const callbacks: BlockCallbacks = {
    onAccept: suppressOfferActions ? undefined : onAccept,
    onDecline,
    accepting,
    suppressOfferActions,
  };

  if (hasBlocks) {
    return (
      <BannerContainer data-banner-slot={containerStyle?.position === 'fixed' ? 'fixed' : 'inline'} style={containerStyle}>
        <BlockDismissButton onClick={onDismiss} aria-label="Dismiss">&times;</BlockDismissButton>
        <BlockContent>{renderBlocks(blocks, displayOffer, callbacks)}</BlockContent>
        {errorMessage && <div style={{ color: '#b42318', fontSize: '13px', padding: '0 16px 12px' }}>{errorMessage}</div>}
        {!hasCtaBlock && (
          <FallbackActions>
            {!suppressOfferActions && (
              <FallbackCTA onClick={onAccept} disabled={accepting}>
                {accepting ? 'Adding...' : offer.ctaText}
              </FallbackCTA>
            )}
            <FallbackDecline onClick={onDecline}>{offer.declineText}</FallbackDecline>
          </FallbackActions>
        )}
      </BannerContainer>
    );
  }

  return (
    <BannerContainer data-banner-slot={containerStyle?.position === 'fixed' ? 'fixed' : 'inline'} style={containerStyle}>
      <ClassicBannerLayout>
        {offer.imageUrl && <BannerImage src={offer.imageUrl} alt={offer.headline ?? 'Offer'} />}
        <BannerContent>
          <BannerHeadline>{offer.headline}</BannerHeadline>
          {errorMessage && <div style={{ color: '#b42318', fontSize: '13px', marginTop: 8 }}>{errorMessage}</div>}
          <BannerPricing>
            <strong>{safePrice(displayOffer.pricingValue)}</strong>
            {' '}
            <span style={{ textDecoration: 'line-through', opacity: 0.7 }}>
              {safePrice(offer.originalPrice)}
            </span>
          </BannerPricing>
        </BannerContent>

        <BannerActions>
          {!suppressOfferActions && (
            <CTAButton onClick={onAccept} disabled={accepting}>
              {accepting ? 'Adding...' : offer.ctaText}
            </CTAButton>
          )}
          <DeclineLink onClick={onDecline}>{offer.declineText}</DeclineLink>
          <DismissButton onClick={onDismiss} aria-label="Dismiss">&times;</DismissButton>
        </BannerActions>
      </ClassicBannerLayout>
    </BannerContainer>
  );
}
